import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionSummary } from '../../../types/subscription';
import {
  selectOverviewSubSort,
  setOverviewSubSort,
  selectOverviewSubView,
  setOverviewSubView,
} from '../../../store/slices/ui';

export const SORT_OPTIONS = [
  'Total Value',
  'Total Stable Profit',
  'Total Asset Profit',
  'Current Stable Profit',
  'Current Asset Profit',
  'Non-Algo Diff',
] as const;
export type SortOption = (typeof SORT_OPTIONS)[number];
export type ViewMode = 'grid' | 'list';

/**
 * Return the value used to *sort* subscriptions for a given sort option.
 *
 * - "Total Value" → absolute total balance (always positive).
 * - All others    → the relative (percentage) profit, so subscriptions
 *                   with the largest %-move come first regardless of size.
 */
export function getSortValue(
  sub: SubscriptionSummary,
  sort: SortOption,
): number {
  switch (sort) {
    case 'Total Stable Profit':
      return sub.profits?.total.stable.relative ?? 0;
    case 'Total Asset Profit':
      return sub.profits?.total.asset.relative ?? 0;
    case 'Current Stable Profit':
      return sub.latestTransaction?.orderType === 'BUY'
        ? sub.profits?.current.stable?.relative ?? 0
        : 0;
    case 'Current Asset Profit':
      return sub.latestTransaction?.orderType === 'SELL'
        ? sub.profits?.current.asset?.relative ?? 0
        : 0;
    case 'Non-Algo Diff':
      return sub.profits?.noAlgorithmicProgressionProfit.relative ?? 0;
    default:
      return sub.totalBalanceInStable;
  }
}

/**
 * Sort options where a 0 means "not currently in that leg" (the current
 * stable/asset profit only exists for BUY / SELL positions respectively).
 * For these, zeros sink to the very bottom — below the negatives — so they
 * don't masquerade as break-even results: e.g. +9, -9, 0, 0, 0.
 */
const ZERO_LAST_SORTS: ReadonlyArray<SortOption> = [
  'Current Stable Profit',
  'Current Asset Profit',
];

/**
 * Canonical subscription comparator used by the dashboard "Sort by" control —
 * the sidebar lists, the content grid, and the P&L leaderboard all order
 * through this so they stay identical.
 *
 * Descending by the *signed* value, so positive results rank above negative
 * ones (e.g. +23, +8, -7, -9, -20) rather than by magnitude. For
 * ZERO_LAST_SORTS, zero values are pushed beneath the negatives.
 */
export function compareSubscriptions(
  a: SubscriptionSummary,
  b: SubscriptionSummary,
  sort: SortOption,
): number {
  const av = getSortValue(a, sort);
  const bv = getSortValue(b, sort);
  if (ZERO_LAST_SORTS.includes(sort)) {
    if (av === 0 && bv === 0) return 0;
    if (av === 0) return 1; // a sinks below b
    if (bv === 0) return -1; // b sinks below a
  }
  return bv - av; // signed, high → low
}

export function sortSubscriptions(
  subs: SubscriptionSummary[],
  sort: SortOption,
): SubscriptionSummary[] {
  return [...subs].sort((a, b) => compareSubscriptions(a, b, sort));
}

interface FilterContextType {
  query: string;
  setQuery: (q: string) => void;
  sort: SortOption;
  setSort: (s: SortOption) => void;
  view: ViewMode;
  setView: (v: ViewMode) => void;
  filteredSubs: SubscriptionSummary[];
  sortedSubs: SubscriptionSummary[];
}

const noop = () => undefined;

const FilterContext = createContext<FilterContextType>({
  query: '',
  setQuery: noop,
  sort: 'Total Value',
  setSort: noop,
  view: 'grid',
  setView: noop,
  filteredSubs: [],
  sortedSubs: [],
});

export const FilterProvider: React.FC<{
  children: React.ReactNode;
  subscriptions: SubscriptionSummary[];
}> = ({ children, subscriptions }) => {
  const [query, setQuery] = useState('');
  const dispatch = useDispatch();
  const sort = useSelector(selectOverviewSubSort) as SortOption;
  const setSort = useCallback(
    (s: SortOption) => {
      dispatch(setOverviewSubSort(s));
    },
    [dispatch],
  );
  const view = useSelector(selectOverviewSubView) as ViewMode;
  const setView = useCallback(
    (v: ViewMode) => {
      dispatch(setOverviewSubView(v));
    },
    [dispatch],
  );

  const filteredSubs = useMemo(() => {
    if (!query) return subscriptions;
    const q = query.toLowerCase();
    return subscriptions.filter(
      (s) =>
        s.baseAsset.toLowerCase().includes(q) ||
        s.quoteAsset.toLowerCase().includes(q) ||
        s.exchange.toLowerCase().includes(q),
    );
  }, [query, subscriptions]);

  const sortedSubs = useMemo(
    () => sortSubscriptions(filteredSubs, sort),
    [filteredSubs, sort],
  );

  return (
    <FilterContext.Provider
      value={{
        query,
        setQuery,
        sort,
        setSort,
        view,
        setView,
        filteredSubs,
        sortedSubs,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = () => useContext(FilterContext);
